import { randomUUID } from "node:crypto";
import { createReadStream, createWriteStream } from "node:fs";
import { rm } from "node:fs/promises";
import { tmpdir } from "node:os";
import path from "node:path";
import { Readable } from "node:stream";
import { pipeline } from "node:stream/promises";
import type { SnapshotTarResult } from "./snapshot-tar.js";

// A hung upload/download must not stall capture forever (which would leave
// the in-flight promise stuck and silently stop all periodic snapshots).
export const SNAPSHOT_HTTP_TIMEOUT_MS = 60_000;

/** Header carrying the tar's BUILD time so the API can refuse to overwrite a
 *  newer capture with a slower, older PUT (see snapshot-routes.ts). */
export const SNAPSHOT_CAPTURED_AT_HEADER = "x-snapshot-captured-at";

export async function putSnapshotToGcs(
  url: string,
  tarResult: SnapshotTarResult,
): Promise<boolean> {
  try {
    // Stream the tar file straight into the request body — the archive is
    // never buffered whole in memory (pods have an OOM history; tars can hit
    // the 500MB route cap). `duplex: "half"` is required by undici for stream
    // bodies and ignored by runtimes that don't need it.
    const body = Readable.toWeb(createReadStream(tarResult.tarPath)) as ReadableStream;
    const res = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/gzip",
        "Content-Length": String(tarResult.sizeBytes),
        [SNAPSHOT_CAPTURED_AT_HEADER]: String(tarResult.capturedAt),
      },
      body,
      duplex: "half",
      signal: AbortSignal.timeout(SNAPSHOT_HTTP_TIMEOUT_MS),
    } as RequestInit);
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Download a snapshot archive to a temp file, streaming (never buffering the
 * whole tar). Returns the temp path on success, null on any failure — the
 * caller owns removing the file.
 */
export async function downloadSnapshotToTemp(url: string): Promise<string | null> {
  const tmpTar = path.join(tmpdir(), `conveyor-snapshot-restore-${randomUUID()}.tar.gz`);
  try {
    const res = await fetch(url, {
      method: "GET",
      signal: AbortSignal.timeout(SNAPSHOT_HTTP_TIMEOUT_MS),
    });
    if (!res.ok || !res.body) return null;
    await pipeline(
      Readable.fromWeb(res.body as import("node:stream/web").ReadableStream),
      createWriteStream(tmpTar),
    );
    return tmpTar;
  } catch {
    await rm(tmpTar, { force: true }).catch(() => {});
    return null;
  }
}
